/**
 * Parse the free-text `dimensions_raw` string carried by indexed historical
 * costing-sheet line items into { width, height, length } inches.
 *
 * The corpus is human-typed, so the shapes vary:
 *   16"W x 40"H              → labelled two-value
 *   74 x 31                  → unlabelled two-value (W x H)
 *   5.5"W x 21"H x 62"L      → labelled three-value
 *   12 X 8 X 40              → unlabelled three-value (W x H x L)
 *   40"H                     → single labelled value
 *   5 1/2" W x 21" H         → fractional inches
 *   3'W x 4'H                → feet, converted to inches
 *
 * Anything it cannot read confidently comes back as `null` — the caller leaves
 * the dimension inputs blank rather than guessing at a number that would end up
 * baked into a rate card.
 */

const SEPARATOR = /\s*[x×*]\s*/i;

// number (optional whole + fraction, or bare fraction), optional unit mark,
// optional axis label (before or after the unit mark), optional trailing dot.
const TOKEN = new RegExp(
  "^" +
    "([whl])?\\s*" + // leading label:  W 16"
    "(\\d+(?:\\.\\d+)?(?:\\s+\\d+\\s*/\\s*\\d+)?|\\d+\\s*/\\s*\\d+)" + // 5, 5.5, 5 1/2, 1/2
    "\\s*" +
    "(\"|''|”|″|in\\.?|inch(?:es)?|'|’|′|ft\\.?|feet|foot)?" + // unit mark
    "\\s*" +
    "([whl])?" + // trailing label: 16"W
    "\\.?" +
    "$",
  "i",
);

const FEET_MARKS = new Set(["'", "’", "′", "ft", "ft.", "feet", "foot"]);

const AXIS_KEY = { w: "width", h: "height", l: "length" };
const AXIS_ORDER = ["width", "height", "length"];

const EMPTY = { width: null, height: null, length: null };

function toNumber(text) {
  const cleaned = text.replace(/\s*\/\s*/g, "/").trim();
  const mixed = cleaned.match(/^(\d+(?:\.\d+)?)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const denom = Number(mixed[3]);
    if (!denom) return null;
    return Number(mixed[1]) + Number(mixed[2]) / denom;
  }
  const fraction = cleaned.match(/^(\d+)\/(\d+)$/);
  if (fraction) {
    const denom = Number(fraction[2]);
    if (!denom) return null;
    return Number(fraction[1]) / denom;
  }
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/**
 * @param {string} raw
 * @returns {{width: number|null, height: number|null, length: number|null}|null}
 */
export function parseDimensions(raw) {
  if (typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text) return null;

  const tokens = text.split(SEPARATOR).filter((part) => part.trim() !== "");
  if (tokens.length === 0 || tokens.length > 3) return null;

  const parsed = [];
  for (const token of tokens) {
    const match = token.trim().match(TOKEN);
    if (!match) return null;

    const leadLabel = match[1];
    const trailLabel = match[4];
    if (leadLabel && trailLabel) return null;

    const value = toNumber(match[2]);
    if (value === null || value < 0) return null;

    const unitMark = (match[3] || "").toLowerCase();
    const inches = FEET_MARKS.has(unitMark) ? value * 12 : value;

    const label = (leadLabel || trailLabel || "").toLowerCase();
    parsed.push({ value: inches, axis: label ? AXIS_KEY[label] : null });
  }

  // A lone unlabelled number is ambiguous — refuse it.
  if (parsed.length === 1 && !parsed[0].axis) return null;

  const dims = { ...EMPTY };
  for (const entry of parsed) {
    if (!entry.axis) continue;
    if (dims[entry.axis] !== null) return null; // same axis twice
    dims[entry.axis] = entry.value;
  }

  // Unlabelled values fall into the remaining slots in W → H → L order.
  const free = AXIS_ORDER.filter((axis) => dims[axis] === null);
  let slot = 0;
  for (const entry of parsed) {
    if (entry.axis) continue;
    const axis = free[slot];
    slot += 1;
    if (!axis) return null;
    dims[axis] = entry.value;
  }

  return dims;
}

/**
 * Short "16×40" style summary of parsed dimensions, for the provenance line.
 * Returns "" when there is nothing worth showing.
 */
export function formatDimsSummary(dims) {
  if (!dims) return "";
  const parts = AXIS_ORDER.map((axis) => dims[axis]).filter(
    (value) => value !== null && value !== undefined,
  );
  if (parts.length === 0) return "";
  return parts.map((value) => Number(value.toFixed(2)).toString()).join("×");
}

export default parseDimensions;
